import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { metricDescriptions } from './metricDescriptions';

export function MetricHelp({ label, metric = label, children }: { label: string; metric?: string; children?: ReactNode }) {
  const help = metricDescriptions[metric];
  const id = useId();
  const anchor = useRef<HTMLSpanElement>(null), bubble = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [open, setOpen] = useState(false), [position, setPosition] = useState({ top: 0, left: 0 });
  const show = () => { clearTimeout(timer.current); setOpen(true); };
  const hide = () => { clearTimeout(timer.current); timer.current = setTimeout(() => { if (!anchor.current?.contains(document.activeElement)) setOpen(false); }, 140); };
  useEffect(() => () => clearTimeout(timer.current), []);
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      if (!anchor.current || !bubble.current) return;
      const a = anchor.current.getBoundingClientRect(), b = bubble.current.getBoundingClientRect();
      setPosition({
        left: Math.max(12, Math.min(a.left, window.innerWidth - b.width - 12)),
        top: Math.max(12, a.bottom + b.height + 12 <= window.innerHeight ? a.bottom + 6 : a.top - b.height - 6),
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => { window.removeEventListener('resize', update); window.removeEventListener('scroll', update, true); };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
    const outside = (e: PointerEvent) => { if (e.target instanceof Node && !anchor.current?.contains(e.target) && !bubble.current?.contains(e.target)) close(); };
    window.addEventListener('keydown', key, true);
    document.addEventListener('pointerdown', outside);
    return () => { window.removeEventListener('keydown', key, true); document.removeEventListener('pointerdown', outside); };
  }, [open]);
  if (!help) return <span className="metric-stack"><span className="metric-label">{label}</span>{children}</span>;
  return <span ref={anchor} className="metric-stack" data-metric-help data-testid={`metric-${metric}`} onMouseEnter={show} onMouseLeave={hide}>
    <button type="button" className="metric-trigger" aria-label={`${label} 설명`} aria-describedby={open ? id : undefined}
      onFocus={show} onBlur={hide} onClick={() => { clearTimeout(timer.current); setOpen(true); }}>
      {label}<span className="metric-info" aria-hidden="true">i</span>
    </button>
    {children}
    {open && createPortal(<div ref={bubble} id={id} role="tooltip" className="metric-tooltip" style={position} onMouseEnter={show} onMouseLeave={hide}>
      <strong>{label}</strong><p>{help}</p>
    </div>, document.body)}
  </span>;
}
